import React from "react";
import { Link } from "react-router-dom";
import {
  Package,
  Droplet,
  FlaskConical,
  Hash,
  Globe2,
  Handshake,
  Cog,
  ArrowRight,
  Package2,
  CalendarClock,
  Tag,
  PackageOpen,
  Layers,
  Layers3,
  Wrench,
  ClipboardCheck,
  SearchCheck,
} from "lucide-react";
import SEO from "@/components/SEO";
import { siteConfig } from "@/config/siteConfig";

const IMG_BASE = "/images/home";
const HERO_ALT =
  "Pharmaceutical blister packaging production line with white tablets sealed in aluminium blister foil on a stainless-steel conveyor";
const SAMPLE_ALT =
  "Pharmaceutical printing samples: blister foil, coded carton, vial, ink swatch, metal stereo plate";

// ---------- primitives ----------
const Section = ({ id, alt = false, className = "", children, testid }) => (
  <section
    id={id}
    data-testid={testid}
    className={`${alt ? "bg-[var(--bg-alt)]" : "bg-white"} py-16 lg:py-20 ${className}`}
  >
    <div className={`mx-auto max-w-[1240px] px-6 lg:px-10 ${className}`}>{children}</div>
  </section>
);
const Eyebrow = ({ children }) => (
  <p className="text-[12px] uppercase tracking-[0.14em] text-[var(--pharma-blue)] font-semibold">
    {children}
  </p>
);
const H2 = ({ children, className = "", ...rest }) => (
  <h2
    className={`font-heading text-[28px] leading-tight lg:text-[38px] lg:leading-[1.1] font-semibold text-[var(--navy)] tracking-tight ${className}`}
    {...rest}
  >
    {children}
  </h2>
);

// ---------- Section 3: 4 solution cards ----------
const SOLUTION_CARDS = [
  {
    id: "blister",
    Icon: Package,
    title: "Blister Printing Solutions",
    desc: "Specialist inks and supporting products for pharmaceutical blister-pack printing applications.",
    products: ["Blister Pack Violet Ink", "Blister Pack Inks-aid"],
    cta: "Explore Blister Solutions",
    href: "/solutions/blister-printing",
  },
  {
    id: "inks",
    Icon: Droplet,
    title: "Specialty Printing Inks",
    desc: "Printing solutions for porous and non-porous substrates and specialised identification applications.",
    products: ["Indelible Ink", "Porous Ink", "Non-Porous Ink"],
    cta: "Explore Printing Inks",
    href: "/solutions/specialty-inks",
  },
  {
    id: "coding",
    Icon: Hash,
    title: "Coding & Marking Consumables",
    desc: "Selected consumables for batch, lot, manufacturing-date and expiry-date coding applications.",
    products: [
      "Solvent HP-Compatible Cartridge",
      "Water-Based HP Black Cartridge",
    ],
    cta: "Explore Coding Solutions",
    href: "/solutions/coding-marking",
  },
  {
    id: "aids",
    Icon: FlaskConical,
    title: "Printing Aids & Maintenance",
    desc: "Supporting products for printing applications and production-line maintenance.",
    products: [
      "Indelible Poly Inks-aid",
      "PVC Reducer",
      "SS Polyfine Inks-aid",
      "Screen Cleaning Aid",
    ],
    cta: "Explore Printing Aids",
    href: "/solutions/printing-aids",
  },
];

// ---------- Section 4: 4-step process ----------
const PROCESS_STEPS = [
  { n: "01", title: "Understand Your Application", desc: "Machine, substrate, current product and print requirement." },
  { n: "02", title: "Product Recommendation", desc: "We review the application and identify an appropriate product for evaluation." },
  { n: "03", title: "Sample & Production Trial", desc: "Evaluate the product under actual production conditions." },
  { n: "04", title: "Commercial Supply", desc: "Following successful evaluation, establish regular commercial supply." },
];

// ---------- Section 5: 4-item credibility strip ----------
const CREDIBILITY_ITEMS = [
  { Icon: Globe2, title: "Dubai-Based International Business", desc: "Commercial and logistics base in Dubai, United Arab Emirates." },
  { Icon: FlaskConical, title: "Specialist Pharmaceutical Focus", desc: "A focused portfolio built around pharmaceutical printing and coding requirements." },
  { Icon: Cog, title: "Application-Based Product Selection", desc: "Recommendations begin with understanding the machine, substrate and application." },
  { Icon: Handshake, title: "International Supply & Distribution", desc: "Supplying pharmaceutical manufacturers and building distribution partnerships across international markets." },
];

// ---------- Section 6: 8 application tiles ----------
const APPLICATION_TILES = [
  { id: "blister-pack-printing", Icon: Package2, label: "Blister Pack Printing" },
  { id: "batch-coding", Icon: Hash, label: "Batch Coding" },
  { id: "manufacturing-expiry-coding", Icon: CalendarClock, label: "Manufacturing & Expiry Date Coding" },
  { id: "lot-identification", Icon: Tag, label: "Lot Identification" },
  { id: "carton-coding", Icon: PackageOpen, label: "Carton Coding" },
  { id: "porous-printing", Icon: Layers, label: "Porous Substrate Printing" },
  { id: "non-porous-printing", Icon: Layers3, label: "Non-Porous Substrate Printing" },
  { id: "printing-line-maintenance", Icon: Wrench, label: "Printing-Line Maintenance" },
];

// ---------- Section 8: Technical Support mini-items ----------
const TS_ITEMS = [
  { Icon: ClipboardCheck, label: "Application Assessment" },
  { Icon: SearchCheck, label: "Substrate Review" },
  { Icon: FlaskConical, label: "Sample & Trial" },
];

// ---------- Section 9: 3 regions ----------
const REGIONS = [
  { name: "Middle East", note: "Regional supply from Dubai." },
  { name: "South Asia", note: "Actively developing partners." },
  { name: "Africa", note: "Actively developing partners." },
];

const Home = () => {
  return (
    <>
      <SEO
        title={siteConfig.TAGLINE}
        description="Specialist pharmaceutical printing inks, coding consumables and production solutions supplied internationally from Dubai for pharmaceutical manufacturing applications."
        path="/"
        ogImage={`${siteConfig.DOMAIN}${IMG_BASE}/hero-blister-line-v2.jpg`}
      />

      {/* ============ Section 1 — Hero ============ */}
      <section className="relative bg-white pt-12 lg:pt-16 pb-16 lg:pb-24 min-h-[640px] flex items-center" data-testid="home-hero">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[minmax(0,55fr)_minmax(0,45fr)] gap-10 lg:gap-14 items-center w-full">
          <div>
            <p className="text-[12px] uppercase tracking-[0.14em] text-[var(--pharma-blue)] font-semibold" data-testid="home-hero-eyebrow">
              Pharmaceutical Printing &amp; Coding
            </p>
            <h1
              className="font-heading mt-4 text-[40px] leading-[1.05] sm:text-[48px] lg:text-[56px] lg:leading-[1.03] font-semibold text-[var(--navy)] tracking-[-0.015em]"
              data-testid="home-hero-h1"
            >
              Specialised Printing &amp; Coding Solutions for Pharmaceutical Manufacturing
            </h1>
            <p className="mt-6 text-[16px] lg:text-[18px] text-[var(--charcoal)] max-w-2xl leading-relaxed" data-testid="home-hero-sub">
              Reliable printing inks, coding consumables and production solutions for pharmaceutical manufacturers worldwide.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/request-sample" className="btn-primary" data-testid="home-hero-cta-sample">Request a Sample</Link>
              <Link to="/contact" className="btn-outline" data-testid="home-hero-cta-contact">Talk to Our Team</Link>
            </div>
            <p
              className="mt-8 text-[12px] uppercase tracking-[0.08em] text-[var(--slate)]"
              data-testid="home-hero-credibility"
            >
              HEADQUARTERED IN DUBAI | SERVING PHARMACEUTICAL MANUFACTURERS INTERNATIONALLY
            </p>
          </div>
          <div>
            <div className="border border-[var(--border)] rounded-[4px] overflow-hidden bg-[var(--bg-alt)]">
              <picture>
                <source type="image/webp" srcSet={`${IMG_BASE}/hero-blister-line-v2.webp`} />
                <img
                  src={`${IMG_BASE}/hero-blister-line-v2.jpg`}
                  alt={HERO_ALT}
                  className="w-full h-auto block"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width="1600"
                  height="1067"
                  data-testid="home-hero-image"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Section 2 — Reliable Printing Starts With… ============ */}
      <Section alt testid="home-approach">
        <div className="max-w-3xl">
          <Eyebrow>Our Approach</Eyebrow>
          <H2 className="mt-4" data-testid="home-approach-h2">
            Reliable Printing Starts With the Right Ink for the Application
          </H2>
          <div className="mt-6 space-y-4 text-[16px] lg:text-[17px] leading-relaxed text-[var(--charcoal)]">
            <p>
              Printing performance depends on more than the ink itself. Substrate characteristics, printing equipment, production speed and operating conditions can all affect adhesion, drying and print consistency.
            </p>
            <p>
              {siteConfig.COMPANY_NAME} provides specialised printing inks, coding consumables and supporting products for pharmaceutical manufacturing applications. Our approach begins by understanding the machine, substrate and production requirement before recommending a product for evaluation.
            </p>
          </div>
          <div className="mt-8">
            <Link to="/contact?type=technical" className="btn-primary" data-testid="home-approach-cta">
              Discuss Your Application
            </Link>
          </div>
        </div>
      </Section>

      {/* ============ Section 3 — 4 solution cards ============ */}
      <Section testid="home-solutions">
        <Eyebrow>Our Solutions</Eyebrow>
        <H2 className="mt-4 uppercase" data-testid="home-solutions-h2">
          Specialist Solutions for Pharmaceutical Manufacturing
        </H2>
        <p className="mt-4 text-[16px] leading-relaxed text-[var(--charcoal)] max-w-3xl" data-testid="home-solutions-intro">
          Focused solutions for pharmaceutical printing, packaging and production coding applications.
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {SOLUTION_CARDS.map(({ id, Icon, title, desc, products, cta, href }) => (
            <div
              key={id}
              className="border border-[var(--border)] rounded-[4px] bg-white p-10 flex flex-col"
              data-testid={`solution-card-${id}`}
            >
              <Icon className="w-10 h-10 text-[var(--pharma-blue)]" strokeWidth={1.5} />
              <h3 className="font-heading mt-5 text-[24px] font-semibold text-[var(--navy)] tracking-tight">
                {title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[var(--slate)]">{desc}</p>
              <ul className="mt-4 space-y-1.5 text-[14px] text-[var(--charcoal)]">
                {products.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <span className="mt-1.5 inline-block w-1 h-1 bg-[var(--pharma-blue)] rounded-full flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link to={href} className="arrow-link mt-6 text-[14px]" data-testid={`solution-card-${id}-cta`}>
                {cta} <ArrowRight className="inline w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ Section 4 — From Application to Production ============ */}
      <Section alt testid="home-process">
        <Eyebrow>Our Process</Eyebrow>
        <H2 className="mt-4" data-testid="home-process-h2">
          From Application to Production
        </H2>
        <p className="mt-4 text-[16px] leading-relaxed text-[var(--charcoal)] max-w-3xl" data-testid="home-process-intro">
          Every production environment is different. Our process is designed to identify an appropriate solution and allow customers to evaluate its performance under their actual production conditions.
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-4 relative">
          {PROCESS_STEPS.map((step, i) => (
            <div key={step.n} className="relative" data-testid={`home-process-step-${step.n}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-full border-2 border-[var(--navy)] text-[var(--navy)] flex items-center justify-center font-heading font-semibold text-[18px] bg-white z-10">
                  {step.n}
                </div>
                {i < PROCESS_STEPS.length - 1 && (
                  <div className="hidden md:block flex-1 h-[1px] bg-[var(--pharma-blue)]/40" />
                )}
              </div>
              <h3 className="font-heading text-[18px] font-semibold text-[var(--navy)]">{step.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[var(--slate)]">{step.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/request-sample" className="btn-primary" data-testid="home-process-cta">
            Request a Sample
          </Link>
        </div>
      </Section>

      {/* ============ Section 5 — 4-item credibility strip ============ */}
      <section className="bg-white py-14 lg:py-16 border-t border-b border-[var(--border)]" data-testid="home-credibility">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {CREDIBILITY_ITEMS.map(({ Icon, title, desc }, i) => (
            <div key={i} data-testid={`home-credibility-item-${i}`}>
              <Icon className="w-10 h-10 text-[var(--pharma-blue)]" strokeWidth={1.5} />
              <h3 className="font-heading mt-4 text-[18px] font-semibold text-[var(--navy)]">{title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[var(--slate)]">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ Section 6 — Solutions for Pharmaceutical Production (8 tiles) ============ */}
      <Section alt testid="home-pharma-applications">
        <div className="max-w-3xl">
          <Eyebrow>Pharmaceutical Focus</Eyebrow>
          <H2 className="mt-4" data-testid="home-pharma-applications-h2">
            Solutions for Pharmaceutical Production
          </H2>
          <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[var(--charcoal)]">
            Our primary focus is pharmaceutical manufacturing, with specialised products supporting printing, packaging, coding and production-line applications.
          </p>
        </div>
        {/* Section 6 banner image (§5 Image A) */}
        <div
          className="mt-10 relative overflow-hidden rounded-[4px] border border-[var(--border)]"
          data-testid="home-pharma-applications-banner"
          style={{ height: "320px" }}
        >
          <picture>
            <source type="image/webp" srcSet={`${IMG_BASE}/pharma-coding-band.webp`} />
            <img
              src={`${IMG_BASE}/pharma-coding-band.jpg`}
              alt="Industrial thermal inkjet coding system printing batch and expiry information on pharmaceutical cartons"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
          </picture>
          <div
            className="absolute inset-0"
            style={{ background: "rgba(10, 37, 64, 0.12)" }}
            aria-hidden="true"
          />
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6" data-testid="home-pharma-applications-grid">
          {APPLICATION_TILES.map(({ id, Icon, label }) => (
            <div
              key={id}
              className="border border-[var(--border)] rounded-[4px] bg-white p-6 lg:p-7 flex flex-col items-start"
              data-testid={`home-app-tile-${id}`}
            >
              <Icon className="w-10 h-10 text-[var(--pharma-blue)]" strokeWidth={1.5} />
              <p className="mt-4 text-[16px] font-semibold text-[var(--navy)] leading-snug">{label}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/industries/pharmaceutical" className="btn-primary" data-testid="home-pharma-applications-cta">
            Explore Pharmaceutical Solutions
          </Link>
        </div>
      </Section>

      {/* ============ Section 7 — Not Sure Which Product You Need? (FIND THE RIGHT INK) ============ */}
      <Section testid="home-find-ink">
        <div className="border border-[var(--border)] rounded-[4px] p-8 lg:p-12 bg-[var(--bg-alt)]">
          <Eyebrow>Application Advisor</Eyebrow>
          <H2 className="mt-4" data-testid="home-find-ink-h2">
            Not Sure Which Product You Need?
          </H2>
          <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[var(--charcoal)] max-w-3xl">
            Tell us about your printing application and we&rsquo;ll review the requirement.
          </p>
          <div className="mt-8">
            <Link
              to="/find-the-right-ink"
              className="btn-primary uppercase tracking-wider"
              data-testid="home-find-ink-cta"
            >
              Find the Right Ink
            </Link>
          </div>
        </div>
      </Section>

      {/* ============ Section 8 — Technical Support summary ============ */}
      <Section alt testid="home-technical-support">
        <div className="max-w-3xl">
          <Eyebrow>Technical Support</Eyebrow>
          <H2 className="mt-4" data-testid="home-technical-support-h2">
            Technical Support Begins With Understanding the Application
          </H2>
          <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[var(--charcoal)]">
            Changing a printing ink or coding consumable involves more than product names. We help review the application before recommending a product.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-technical-support-items">
          {TS_ITEMS.map(({ Icon, label }) => (
            <div
              key={label}
              className="border border-[var(--border)] rounded-[4px] bg-white p-5 flex items-center gap-4"
              data-testid={`home-ts-item-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon className="w-6 h-6 text-[var(--pharma-blue)] shrink-0" strokeWidth={1.5} />
              <p className="text-[14px] font-semibold text-[var(--navy)]">{label}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/technical-support" className="btn-primary" data-testid="home-technical-support-cta">
            Explore Technical Support
          </Link>
        </div>
      </Section>

      {/* ============ Section 9 — Developing Our International Distribution Network ============ */}
      <Section testid="home-distribution">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,30fr)_minmax(0,70fr)] gap-8 lg:gap-12 items-start">
          <div
            className="border border-[var(--border)] rounded-[4px] overflow-hidden bg-[var(--bg-alt)]"
            data-testid="home-distribution-image"
          >
            <picture>
              <source type="image/webp" srcSet={`${IMG_BASE}/blister-foil-close-up.webp`} />
              <img
                src={`${IMG_BASE}/blister-foil-close-up.jpg`}
                alt="Close-up of printed aluminium blister foil showing batch coding and expiry date on a pharmaceutical packaging line"
                loading="lazy"
                decoding="async"
                width="1264"
                height="848"
                className="w-full h-auto block"
              />
            </picture>
          </div>
          <div>
            <Eyebrow>Partner Network</Eyebrow>
            <H2 className="mt-4 uppercase" data-testid="home-distribution-h2">
              Developing Our International Distribution Network
            </H2>
            <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[var(--charcoal)]">
              We are building relationships with experienced distributors and technical partners serving pharmaceutical manufacturers across international markets.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="home-distribution-regions">
              {REGIONS.map((r) => (
                <div
                  key={r.name}
                  className="border border-[var(--border)] rounded-[4px] bg-white p-6"
                  data-testid={`home-region-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <h3 className="font-heading text-[17px] font-semibold text-[var(--navy)]">{r.name}</h3>
                  <p className="mt-2 text-[14px] text-[var(--slate)]">{r.note}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[14px] italic text-[var(--slate)]" data-testid="home-distribution-disclaimer">
              We are actively developing our international distribution network. These are target markets and development focus areas — not existing exclusive territories.
            </p>
            <div className="mt-8">
              <Link to="/distributors" className="btn-outline" data-testid="home-distribution-cta">
                Become a Distribution Partner
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* ============ Section 10 — Final sample CTA ============ */}
      <Section alt testid="home-final-cta">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>Get Started</Eyebrow>
            <h2 className="font-heading mt-4 text-[32px] leading-tight lg:text-[40px] lg:leading-[1.1] font-semibold text-[var(--navy)] tracking-tight" data-testid="home-final-cta-h2">
              Looking for an Alternative Printing or Coding Solution?
            </h2>
            <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[var(--charcoal)]">
              Tell us what you currently use, your machine and substrate, and the result you need to achieve. Our team will review the application and discuss a suitable product for evaluation.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/request-sample" className="btn-primary px-6 py-3" data-testid="home-final-cta-sample">
                Request a Sample
              </Link>
              <Link to="/contact" className="btn-outline px-6 py-3" data-testid="home-final-cta-contact">
                Talk to Our Team
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[var(--border)] rounded-[4px] overflow-hidden bg-white">
              <picture>
                <source type="image/webp" srcSet={`${IMG_BASE}/sample-flatlay.webp`} />
                <img
                  src={`${IMG_BASE}/sample-flatlay.jpg`}
                  alt={SAMPLE_ALT}
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                  width="1264"
                  height="848"
                />
              </picture>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Home;
